import React, { useMemo } from 'react';
import type { CapabilitiesState, CapChannelId } from '../../receptionist-settings/types';
import {
  RCPT_SETTINGS_CAP_ACTIONS_FLAT,
  RCPT_SETTINGS_CAP_CHANNELS,
  RCPT_SETTINGS_CAP_GROUPS,
} from '../../receptionist-settings/mockData';
import {
  SettingsFieldRow,
  SettingsPageHeader,
  SettingsSectionHeader,
  SettingsSelect,
  SettingsToggle,
} from './SettingsShared';

export interface CapabilitiesSettingsPageProps {
  state: CapabilitiesState;
  setState: React.Dispatch<React.SetStateAction<CapabilitiesState>>;
}

export function CapabilitiesSettingsPage({ state, setState }: CapabilitiesSettingsPageProps) {
  const emptyChannels = (): Record<CapChannelId, boolean> => ({
    call: false,
    sms: false,
    email: false,
    chat: false,
  });

  const toggleAction = (actionId: string, channelId: CapChannelId, value: boolean) => {
    setState((s) => ({
      ...s,
      actions: {
        ...s.actions,
        [actionId]: { ...(s.actions[actionId] ?? emptyChannels()), [channelId]: value },
      },
    }));
  };

  const counts = useMemo(() => {
    const c: Record<CapChannelId, number> = { call: 0, sms: 0, email: 0, chat: 0 };
    RCPT_SETTINGS_CAP_ACTIONS_FLAT.forEach((id) => {
      const row = state.actions[id];
      if (!row) return;
      RCPT_SETTINGS_CAP_CHANNELS.forEach((ch) => {
        if (row[ch.id]) c[ch.id]++;
      });
    });
    return c;
  }, [state.actions]);

  const total = RCPT_SETTINGS_CAP_ACTIONS_FLAT.length;

  return (
    <>
      <SettingsPageHeader
        eyebrow="Setup · capabilities"
        title="What Elva is"
        italic="allowed to do"
        description="Each row is one capability; each column is one channel. Turning off 'Cancel an appointment' for Email means Elva redirects email cancellation requests to the phone."
      />

      <div className="set-fade-up set-d-1 mb-[26px] grid grid-cols-4 gap-3">
        {RCPT_SETTINGS_CAP_CHANNELS.map((ch) => (
          <div
            key={ch.id}
            className="rounded-xl border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-3.5 shadow-[var(--sh-1)]"
          >
            <div className="set-eyebrow mb-1.5 text-[10px]">{ch.label}</div>
            <div className="flex items-baseline gap-1.5">
              <span
                className="font-[var(--font-display)] text-[32px] font-semibold leading-none tracking-[-0.025em] tabular-nums"
                style={{ color: 'var(--set-green-deep)' }}
              >
                {counts[ch.id]}
              </span>
              <span className="font-[var(--font-mono)] text-[11px] text-[var(--ink-3)]">/{total}</span>
            </div>
          </div>
        ))}
      </div>

      {RCPT_SETTINGS_CAP_GROUPS.map((group, gi) => (
        <section
          key={group.id}
          className="set-fade-up mb-[22px]"
          style={{ animationDelay: `${(gi + 2) * 70}ms` }}
        >
          <div className="set-card">
            <SettingsSectionHeader num={group.num} title={group.title} sub={group.sub} />
            <div
              className="grid grid-cols-[1fr_64px_64px_64px_64px] items-center gap-3.5 border-b border-[var(--cream-2)] px-[22px] py-2.5 pl-6"
              style={{ background: 'var(--surface-mute)' }}
            >
              <div className="set-eyebrow text-[10px]">Action</div>
              {RCPT_SETTINGS_CAP_CHANNELS.map((ch) => (
                <div key={ch.id} className="set-eyebrow text-center text-[10px] text-[var(--ink-2)]">
                  {ch.short}
                </div>
              ))}
            </div>
            {group.actions.map((action, ai) => (
              <div
                key={action.id}
                className={[
                  'grid grid-cols-[1fr_64px_64px_64px_64px] items-center gap-3.5 px-[22px] py-3.5 pl-6',
                  ai === group.actions.length - 1 ? '' : 'border-b border-[var(--cream-2)]',
                ].join(' ')}
              >
                <div className="min-w-0">
                  <div className="mb-0.5 text-[13.5px] font-medium leading-snug tracking-[-0.005em] text-[var(--ink-0)]">
                    {action.label}
                  </div>
                  <div className="text-[11.5px] tracking-[-0.005em] text-[var(--ink-2)]">{action.note}</div>
                </div>
                {RCPT_SETTINGS_CAP_CHANNELS.map((ch) => (
                  <div key={ch.id} className="flex justify-center">
                    <SettingsToggle
                      value={state.actions[action.id]?.[ch.id] || false}
                      onChange={(v) => toggleAction(action.id, ch.id, v)}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>
      ))}

      <section className="set-fade-up set-d-5 mb-[26px]">
        <div className="set-card">
          <SettingsSectionHeader
            num="04"
            title="How should Elva quote prices?"
            sub="Different practices have different pricing transparency norms."
          />
          <SettingsFieldRow
            title="Quote style"
            description="Starting-at gives a floor without ceiling; Range invites comparison; Refer-to-staff avoids quoting entirely."
            last
          >
            <SettingsSelect
              value={state.quoteStyle || 'starting-at'}
              onChange={(v) => setState((s) => ({ ...s, quoteStyle: v }))}
              width={240}
              options={[
                { value: 'starting-at', label: 'Starting at…' },
                { value: 'exact', label: 'Exact price' },
                { value: 'range', label: 'Range (low–high)' },
                { value: 'defer', label: 'Refer to staff' },
              ]}
            />
          </SettingsFieldRow>
        </div>
      </section>
    </>
  );
}
